import React, { useState, Fragment } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import PhoneInput from "react-phone-input-2";
import { CheckCircle2, PlayCircle, Sparkles } from "lucide-react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import { toast } from "react-toastify";

// ── Shared Reusable Form Field Component (Online QuickBooks UI Style) ──
const FormField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  error,
  required = false,
  type = "text",
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`border rounded-xl px-3.5 py-2.5 text-sm text-gray-800 outline-none transition-all focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] ${
        error ? "border-red-400 bg-red-50/30" : "border-gray-200 bg-white"
      }`}
    />
    {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
  </div>
);

export default function QBDCustomerAdd() {
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [createdCustomerName, setCreatedCustomerName] = useState("");
  const [isCopied, setIsCopied] = useState(false);

  // ── Exact API Payload Form State (NO extraneous fields) ──
  const [formData, setFormData] = useState({
    name: "",
    companyName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    billAddress: {
      addr1: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
    shipAddress: {
      addr1: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
  });

  const [errors, setErrors] = useState({});

  // ── Handlers ──
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleAddressChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  // Copy Billed To Address -> Shipped To Address
  const copyBillToShip = () => {
    setFormData((prev) => ({
      ...prev,
      shipAddress: { ...prev.billAddress },
    }));
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  // Validate only required fields
  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) {
      errs.name = "Customer Name is required (unique in QB)";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Save Customer Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    loader(true);

    // ── Exact API Payload Structure ──
    const payload = {
      name: formData.name.trim(),
      companyName: formData.companyName.trim(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      billAddress: {
        addr1: formData.billAddress.addr1.trim(),
        city: formData.billAddress.city.trim(),
        state: formData.billAddress.state.trim(),
        postalCode: formData.billAddress.postalCode.trim(),
        country: formData.billAddress.country.trim(),
      },
      shipAddress: {
        addr1: formData.shipAddress.addr1.trim(),
        city: formData.shipAddress.city.trim(),
        state: formData.shipAddress.state.trim(),
        postalCode: formData.shipAddress.postalCode.trim(),
        country: formData.shipAddress.country.trim(),
      },
    };

    try {
      const res = await ApiClient.post("qb-desktop/create/customer", payload);
      if (res?.success || res?.status === 200 || res?.code === 200) {
        setCreatedCustomerName(payload.name);
        setShowQueueModal(true);
      } else {
        toast.error(res?.message || res?.error || "Failed to create customer");
      }
    } catch (err) {
      console.error("Error creating customer:", err);
      toast.error(err?.response?.data?.message || err?.message || "An error occurred while creating customer");
    } finally {
      loader(false);
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      companyName: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      billAddress: { addr1: "", city: "", state: "", postalCode: "", country: "" },
      shipAddress: { addr1: "", city: "", state: "", postalCode: "", country: "" },
    });
    setErrors({});
    setShowQueueModal(false);
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px] mx-auto py-6 px-4">
        {/* Top Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
              <Link to="/qb-desktop/customers" className="hover:text-[#115B9D] transition-colors">
                Customers
              </Link>
              <span>/</span>
              <span className="text-gray-800 font-medium">Create Customer</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Create Customer</h1>
            <p className="text-xs sm:text-sm text-gray-600">QuickBooks Desktop Customer Sync</p>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/qb-desktop/customers"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
            >
              Cancel
            </Link>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#115B9D] hover:bg-[#0c477d] text-white text-sm font-semibold rounded-xl shadow-sm transition-all active:scale-95 disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
              {submitting ? "Saving..." : "Save Customer"}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Customer Details */}
          <div className="w-full border rounded-2xl bg-white p-5 lg:p-8 space-y-4">
            <div className="border-b border-gray-100 pb-3">
              <h2 className="text-base font-semibold text-gray-900">Basic & Contact Details</h2>
              <p className="text-xs text-gray-500">Essential customer details required for QuickBooks Desktop</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField
                label="Customer Name"
                name="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g. Mark John"
                error={errors.name}
                required
              />

              <FormField
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={(e) => handleInputChange("companyName", e.target.value)}
                placeholder="e.g. ABC Technologies"
              />

              <FormField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                placeholder="e.g. Mark"
              />

              <FormField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                placeholder="e.g. John"
              />

              <FormField
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="e.g. mark@yopmail.com"
              />

              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Phone Number
                </label>
                <PhoneInput
                  country={"us"}
                  enableSearch={true}
                  placeholder="e.g. 8990711890"
                  value={formData.phone}
                  onChange={(phone) => handleInputChange("phone", phone)}
                  buttonClass="!border !border-gray-200 !bg-gray-50 !rounded-l-xl"
                  inputClass="!w-full border !border-gray-200 bg-white !rounded-xl !h-[42px] text-sm text-gray-800 outline-none transition-all focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] !pl-12"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Addresses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Billing Address */}
            <div className="w-full border rounded-2xl bg-white p-5 lg:p-6 space-y-4">
              <div className="border-b border-gray-100 pb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Billing Address</h2>
                  <p className="text-xs text-gray-500">Bill Address Details</p>
                </div>
              </div>

              <div className="space-y-3">
                <FormField
                  label="Street Address (Addr1)"
                  name="bill_addr1"
                  value={formData.billAddress.addr1}
                  onChange={(e) => handleAddressChange("billAddress", "addr1", e.target.value)}
                  placeholder="e.g. 221B Baker Street"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField
                    label="City"
                    name="bill_city"
                    value={formData.billAddress.city}
                    onChange={(e) => handleAddressChange("billAddress", "city", e.target.value)}
                    placeholder="e.g. London"
                  />
                  <FormField
                    label="State"
                    name="bill_state"
                    value={formData.billAddress.state}
                    onChange={(e) => handleAddressChange("billAddress", "state", e.target.value)}
                    placeholder="e.g. LN"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField
                    label="Postal Code"
                    name="bill_postalCode"
                    value={formData.billAddress.postalCode}
                    onChange={(e) => handleAddressChange("billAddress", "postalCode", e.target.value)}
                    placeholder="e.g. 123456"
                  />
                  <FormField
                    label="Country"
                    name="bill_country"
                    value={formData.billAddress.country}
                    onChange={(e) => handleAddressChange("billAddress", "country", e.target.value)}
                    placeholder="e.g. United Kingdom"
                  />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="w-full border rounded-2xl bg-white p-5 lg:p-6 space-y-4">
              <div className="border-b border-gray-100 pb-3 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Shipping Address</h2>
                  <p className="text-xs text-gray-500">Ship Address Details</p>
                </div>
                <button
                  type="button"
                  onClick={copyBillToShip}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${
                    isCopied
                      ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                      : "text-[#115B9D] hover:underline bg-[#115B9D]/10"
                  }`}
                >
                  {isCopied ? "Copied!" : "Copy Billing Address"}
                </button>
              </div>

              <div className="space-y-3">
                <FormField
                  label="Street Address (Addr1)"
                  name="ship_addr1"
                  value={formData.shipAddress.addr1}
                  onChange={(e) => handleAddressChange("shipAddress", "addr1", e.target.value)}
                  placeholder="e.g. 221B Baker Street"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField
                    label="City"
                    name="ship_city"
                    value={formData.shipAddress.city}
                    onChange={(e) => handleAddressChange("shipAddress", "city", e.target.value)}
                    placeholder="e.g. London"
                  />
                  <FormField
                    label="State"
                    name="ship_state"
                    value={formData.shipAddress.state}
                    onChange={(e) => handleAddressChange("shipAddress", "state", e.target.value)}
                    placeholder="e.g. LN"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField
                    label="Postal Code"
                    name="ship_postalCode"
                    value={formData.shipAddress.postalCode}
                    onChange={(e) => handleAddressChange("shipAddress", "postalCode", e.target.value)}
                    placeholder="e.g. 123456"
                  />
                  <FormField
                    label="Country"
                    name="ship_country"
                    value={formData.shipAddress.country}
                    onChange={(e) => handleAddressChange("shipAddress", "country", e.target.value)}
                    placeholder="e.g. United Kingdom"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Link
              to="/qb-desktop/customers"
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#115B9D] hover:bg-[#0c477d] text-white text-sm font-semibold rounded-xl shadow-sm transition-all active:scale-95 disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
              {submitting ? "Saving..." : "Save Customer"}
            </button>
          </div>
        </form>
      </div>

      {/* ── Queue Created Sync Popup Modal (Matches Desktop Estimate Modal UI) ── */}
      <Transition appear show={showQueueModal} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={() => setShowQueueModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 border border-gray-200 text-left">
                  {/* Modal Header */}
                  <div className="flex items-start justify-between mb-4 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-xl bg-[#F0FEF4] border border-[#0a7822a6]/20 text-[#008236] flex items-center justify-center shrink-0">
                        <CheckCircle2 className="size-5" />
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          Customer Queue Created Successfully!
                        </Dialog.Title>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Queued for QuickBooks Desktop Web Connector
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50/60 border border-blue-100 rounded-xl text-xs text-slate-700">
                      Customer <strong className="text-gray-900">"{createdCustomerName}"</strong> has been queued for synchronization.
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                      <p className="text-xs font-semibold text-[#115B9D] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <PlayCircle className="size-4" /> Sync Instructions
                      </p>

                      <div className="space-y-2.5">
                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            1
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            If <strong>Auto-Run</strong> is not enabled in Web Connector, open <strong>QuickBooks Web Connector</strong> on your PC.
                          </p>
                        </div>

                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            2
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Check the box <strong className="text-[#115B9D]">[ ✓ ]</strong> next to your application name and click <strong className="text-[#008236]">"Update Selected"</strong> to manually sync this customer.
                          </p>
                        </div>

                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            3
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Once synced, the customer details and ListID will be updated in your desktop customer listing.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200/80 flex items-start gap-2.5 text-xs text-amber-900 leading-5">
                      <Sparkles className="size-4 text-amber-600 mt-0.5 shrink-0" />
                      <span>
                        <strong>Pro Tip:</strong> Enable the <strong>Auto-Run</strong> checkbox in Web Connector to automatically sync new customers every 15 minutes.
                      </span>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="mt-5 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="text-xs font-semibold text-gray-600 hover:text-gray-800 px-3 py-2"
                    >
                      Create Another
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigate("/qb-desktop/queue-status")}
                        className="text-xs font-semibold text-[#115B9D] bg-[#115B9D]/10 hover:bg-[#115B9D]/20 px-4 py-2.5 rounded-xl transition-all"
                      >
                        Queue Status
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate("/qb-desktop/customers")}
                        className="inline-flex items-center justify-center gap-2 bg-[#115B9D] hover:opacity-90 text-white text-xs font-medium px-5 py-2.5 rounded-xl transition-all"
                      >
                        Okay, Got it!
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}
